import type { JavaScriptVariable } from '@unifyapps/defs/types/javascriptVariable';
import { useCallback } from 'react';
import { EMPTY_OBJECT } from '../../../consts/empty';
import { useActionsContext } from '../../context/ActionsProvider';
import { usePageInstanceId } from '../../context/PageInstanceIdProvider';
import { useRegistryContext } from '../../context/RegistryProvider';
import InterfacePageHelper from '../../helper/InterfacePage';
import PageFunctionHelper from '../../helper/PageFunctionHelper';
import { useGetGlobalAppStateStoreState } from '../../stores/app/GlobalAppStateStore';
import {
  useGetAppDataSourceRecordStoreState,
  useGetDataSourceRecords,
} from '../../stores/DataSourceRecordStore';
import { useGlobalStateStore } from '../../stores/GlobalStateStore';
import {
  getActivePage,
  getBlocksFromPage,
  useGetAppInterfaceStoreState,
  useGetInterfaceStoreState,
} from '../../stores/InterfaceStore';
import type { ComputeContextMode } from './const';
import { getContextActions } from './utils/actions';
import {
  getBlocksMethods,
  getDataSourcesMethods,
  getLocalStorageMethods,
  getPageFunctionsMethods,
  getPageVariablesMethods,
  getSessionStorageMethods,
} from './utils/methods';

export function useGetPageInterfaceEntityMethods({
  mode,
  getComputeContext,
}: {
  mode: ComputeContextMode;
  getComputeContext?: () => Record<string, unknown>;
}) {
  const instanceId = usePageInstanceId();
  const contextualStore = useGlobalStateStore();
  const { getGlobalAppStateStoreState } = useGetGlobalAppStateStoreState();
  const { getInterfaceStoreState } = useGetInterfaceStoreState();
  const { getAppInterfaceStoreState } = useGetAppInterfaceStoreState();
  const { getDataSourceRecords } = useGetDataSourceRecords();
  const { registry } = useRegistryContext();
  const { doAction: onAction } = useActionsContext();

  const getBlockMethodsForComputeContext = useCallback(() => {
    if (mode === 'readonly') return EMPTY_OBJECT;

    const device = getGlobalAppStateStoreState().deviceDetails.device;
    const interfaceState = getInterfaceStoreState();
    const applicationType = interfaceState.interfaceRecord.properties.manifest?.type;
    const blocks = getBlocksFromPage(device, interfaceState);
    const blockStates = contextualStore.getState().blocks ?? {};

    return getBlocksMethods({
      blocks,
      blockStates,
      onAction,
      blockRegistry: registry,
      applicationType,
      instanceId,
    });
  }, [
    instanceId,
    contextualStore,
    getGlobalAppStateStoreState,
    getInterfaceStoreState,
    onAction,
    registry,
    mode,
  ]);

  const getDataSourceMethodsForComputeContext = useCallback(() => {
    if (mode === 'readonly') return EMPTY_OBJECT;

    const dataSources = getDataSourceRecords();

    return getDataSourcesMethods({
      dataSourceIds: Object.keys(dataSources),
      onAction,
      instanceId,
    });
  }, [getDataSourceRecords, onAction, instanceId, mode]);

  const getPageVariableMethodsForComputeContext = useCallback((): Record<
    string,
    JavaScriptVariable
  > => {
    if (mode === 'readonly') return EMPTY_OBJECT;

    const activePage = getActivePage(getInterfaceStoreState);
    const pageVariables = InterfacePageHelper.getPageVariables(activePage);

    return getPageVariablesMethods({
      pageVariableIds: Object.keys(pageVariables ?? {}),
      onAction,
      instanceId,
    });
  }, [instanceId, getInterfaceStoreState, onAction, mode]);

  const getAppPageVariableMethodsForComputeContext = useCallback((): Record<
    string,
    JavaScriptVariable
  > => {
    if (mode === 'readonly') return EMPTY_OBJECT;

    const activeInterfacePage = getActivePage(getAppInterfaceStoreState);
    const pageVariables = activeInterfacePage?.properties.pageVariables;

    return getPageVariablesMethods({
      pageVariableIds: Object.keys(pageVariables ?? {}),
      onAction,
    });
  }, [getAppInterfaceStoreState, onAction, mode]);

  const getLocalStorageMethodsForComputeContext = useCallback(() => {
    if (mode === 'readonly') return EMPTY_OBJECT;

    return getLocalStorageMethods({ onAction, instanceId });
  }, [onAction, instanceId, mode]);

  const getSessionStorageMethodsForComputeContext = useCallback(() => {
    if (mode === 'readonly') return EMPTY_OBJECT;

    return getSessionStorageMethods({ onAction, instanceId });
  }, [onAction, instanceId, mode]);

  const getCombinedPageVariableMethodsForComputeContext = useCallback(() => {
    const pageVariableMethods = getPageVariableMethodsForComputeContext();
    const appPageVariableMethods = getAppPageVariableMethodsForComputeContext();
    // keep the readonly fast path reference-stable — a fresh spread here would
    // re-introduce the identity churn the EMPTY_OBJECT returns just removed
    if (pageVariableMethods === EMPTY_OBJECT && appPageVariableMethods === EMPTY_OBJECT) {
      return EMPTY_OBJECT;
    }
    return { ...pageVariableMethods, ...appPageVariableMethods };
  }, [getPageVariableMethodsForComputeContext, getAppPageVariableMethodsForComputeContext]);

  const getPageFunctionMethodsForComputeContext = useCallback(() => {
    if (mode === 'readonly') return EMPTY_OBJECT;

    const activePage = getActivePage(getInterfaceStoreState);
    const pageFunctions = InterfacePageHelper.getPageFunctions(activePage);
    const manualPageFunctions = PageFunctionHelper.getManualFncs(pageFunctions ?? {});
    const manualPageFunctionIds = manualPageFunctions.map((fn) => fn.id);

    return getPageFunctionsMethods({
      pageFunctionIds: manualPageFunctionIds,
      onAction,
      instanceId,
      getComputeContext,
    });
  }, [instanceId, getInterfaceStoreState, onAction, mode, getComputeContext]);

  const getAppPageFunctionMethodsForComputeContext = useCallback(() => {
    if (mode === 'readonly') return EMPTY_OBJECT;

    const activeInterfacePage = getActivePage(getAppInterfaceStoreState);
    const pageFunctions = activeInterfacePage?.properties.pageFunctions;
    const manualPageFunctions = PageFunctionHelper.getManualFncs(pageFunctions ?? {});
    const manualPageFunctionIds = manualPageFunctions.map((fn) => fn.id);

    return getPageFunctionsMethods({
      pageFunctionIds: manualPageFunctionIds,
      onAction,
      getComputeContext,
    });
  }, [getAppInterfaceStoreState, onAction, mode, getComputeContext]);

  const getCombinedPageFunctionMethodsForComputeContext = useCallback(() => {
    const pageFunctionMethods = getPageFunctionMethodsForComputeContext();
    const appPageFunctionMethods = getAppPageFunctionMethodsForComputeContext();
    // same reference-stability rationale as the page-variable combiner above
    if (pageFunctionMethods === EMPTY_OBJECT && appPageFunctionMethods === EMPTY_OBJECT) {
      return EMPTY_OBJECT;
    }
    return { ...pageFunctionMethods, ...appPageFunctionMethods };
  }, [getPageFunctionMethodsForComputeContext, getAppPageFunctionMethodsForComputeContext]);

  const getActionsForComputeContext = useCallback(() => {
    // actions (emitPageEvent/showNotification) are side-effect dispatchers, so they
    // only make sense in mutable context. Gating them here — like every sibling
    // method getter above — keeps the readonly base action-free, which is what lets
    // it stay shareable across every readonly block on the page.
    if (mode === 'readonly') return EMPTY_OBJECT;
    return getContextActions({ onAction, instanceId, scope: 'page' });
  }, [onAction, instanceId, mode]);

  return {
    getBlockMethodsForComputeContext,
    getDataSourceMethodsForComputeContext,
    getPageVariableMethodsForComputeContext: getCombinedPageVariableMethodsForComputeContext,
    getPageFunctionMethodsForComputeContext: getCombinedPageFunctionMethodsForComputeContext,
    getLocalStorageMethodsForComputeContext,
    getSessionStorageMethodsForComputeContext,
    getActionsForComputeContext,
  };
}

export function useGetAppInterfaceEntityMethods({
  mode,
  getComputeContext,
}: {
  mode: ComputeContextMode;
  getComputeContext?: () => Record<string, unknown>;
}) {
  const { getAppDataSourceRecordStoreState } = useGetAppDataSourceRecordStoreState();
  const { doAction: onAction } = useActionsContext();
  const { getAppInterfaceStoreState } = useGetAppInterfaceStoreState();

  const getDataSourceMethodsForAppComputeContext = useCallback(() => {
    if (mode === 'readonly') return EMPTY_OBJECT;

    const appDataSourceIds = Object.keys(getAppDataSourceRecordStoreState().dataSources);

    return getDataSourcesMethods({
      dataSourceIds: appDataSourceIds,
      onAction,
    });
  }, [getAppDataSourceRecordStoreState, onAction, mode]);

  const getLocalStorageMethodsForAppComputeContext = useCallback(() => {
    if (mode === 'readonly') return EMPTY_OBJECT;

    return getLocalStorageMethods({ onAction });
  }, [onAction, mode]);

  const getSessionStorageMethodsForAppComputeContext = useCallback(() => {
    if (mode === 'readonly') return EMPTY_OBJECT;

    return getSessionStorageMethods({ onAction });
  }, [onAction, mode]);

  const getPageVariableMethodsForAppComputeContext = useCallback((): Record<
    string,
    JavaScriptVariable
  > => {
    const activeInterfacePage = getActivePage(getAppInterfaceStoreState);
    const pageVariables = activeInterfacePage?.properties.pageVariables;
    if (mode === 'readonly') return pageVariables ?? {};

    return getPageVariablesMethods({
      pageVariableIds: Object.keys(pageVariables ?? {}),
      onAction,
    });
  }, [getAppInterfaceStoreState, onAction, mode]);

  const getPageFunctionMethodsForAppComputeContext = useCallback(() => {
    if (mode === 'readonly') return EMPTY_OBJECT;

    const activeInterfacePage = getActivePage(getAppInterfaceStoreState);
    const pageFunctions = activeInterfacePage?.properties.pageFunctions;
    const manualPageFunctions = PageFunctionHelper.getManualFncs(pageFunctions ?? {});
    const manualPageFunctionIds = manualPageFunctions.map((fn) => fn.id);

    return getPageFunctionsMethods({
      pageFunctionIds: manualPageFunctionIds,
      onAction,
      getComputeContext,
    });
  }, [getAppInterfaceStoreState, onAction, mode, getComputeContext]);

  const getActionsForAppComputeContext = useCallback(() => {
    // same rationale as the page-scope actions getter: mutable-only, so the readonly
    // base carries no actions and stays shareable.
    if (mode === 'readonly') return EMPTY_OBJECT;
    return getContextActions({ onAction, scope: 'app' });
  }, [onAction, mode]);

  return {
    getDataSourceMethodsForAppComputeContext,
    getLocalStorageMethodsForAppComputeContext,
    getSessionStorageMethodsForAppComputeContext,
    getPageVariableMethodsForAppComputeContext,
    getPageFunctionMethodsForAppComputeContext,
    getActionsForAppComputeContext,
  };
}
